import { createSelector, createSlice } from "@reduxjs/toolkit"
import { SidePanelMode } from "./constants"
import { RootState } from "integrations/redux"
import { selectAllEntities, selectAllEntitiesMap, selectPdfEntitiesMap } from "./slice"
import { selectPDFJobsMap } from "plugins/PDFv2/PDFSlice"
import { SidePanelLayerOrGroup, sidePanelMinWidth } from "@nova/constants"
import { getSiblings } from "./utils"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"

export const layersSortOptions = ["None", "Name", "Kind", "Date Created"] as const
export type LayersSortOptions = (typeof layersSortOptions)[number]

export const assetsSortOptions = ["Name", "Date Created", "Size"] as const
export type AssetsSortOptions = (typeof assetsSortOptions)[number]

export const pdfsSortOptions = ["None", "Name"] as const
export type PDFsSortOptions = (typeof pdfsSortOptions)[number]

type SidePanelState = {
    isSidePanelCollapsed: boolean
    sidePanelWidth: number
    sidePanelMode: SidePanelMode
    isSearchAndFilterCollapsed: boolean
    searchLayersValue: string
    sortLayersValue: LayersSortOptions
    filterLayersValue: string[]
    filterLayersCreatorId: string | undefined
    searchAssetsValue: string
    sortAssetsValue: AssetsSortOptions
    filterAssetsValue: string[]
    searchPDFsValue: string
    sortPDFsValue: PDFsSortOptions
    filterPDFsValue: string[]
    searchArchivedLayersValue: string
    selectedArchivedLayerIds: string[]
    isUnarchiving: boolean

    selectedLayerIds: string[]
    renamingLayerId: string | undefined
    renamingInspectorPanelId: string | undefined
    activeLayerId: string | undefined
    activeFeatureId: NovaPluginIds | undefined
}

const initialState: SidePanelState = {
    isSidePanelCollapsed: false,
    sidePanelWidth: sidePanelMinWidth,
    sidePanelMode: "Layers",
    isSearchAndFilterCollapsed: true,
    searchLayersValue: "",
    sortLayersValue: "None",
    filterLayersValue: [],
    filterLayersCreatorId: undefined,
    searchAssetsValue: "",
    sortAssetsValue: "Name",
    filterAssetsValue: [],
    searchPDFsValue: "",
    sortPDFsValue: "None",
    filterPDFsValue: [],
    searchArchivedLayersValue: "",
    selectedArchivedLayerIds: [],
    isUnarchiving: false,

    selectedLayerIds: [],
    renamingLayerId: undefined,
    renamingInspectorPanelId: undefined,
    activeLayerId: undefined,
    activeFeatureId: undefined,
}

const sliceName = "sidePanel"
const {
    reducer,
    actions: {
        resetSidePanelState,
        collapseSidePanel,
        expandSidePanel,
        setSidePanelWidth,
        setSidePanelMode,
        expandSearchAndFilter,
        collapseSearchAndFilter,
        setSearchLayersValue,
        setSortLayersValue,
        setFilterLayersValue,
        setFilterLayersCreatorId,
        setSearchAssetsValue,
        setSortAssetsValue,
        setFilterAssetsValue,
        setSearchPDFsValue,
        setSortPDFsValue,
        setFilterPDFsValue,
        setSearchArchivedLayersValue,
        addArchivedLayersToSelection,
        removeArchivedLayersFromSelection,
        setIsUnarchiving,
        selectSingleLayer,
        selectMultipleLayers,
        addLayerToSelection,
        removeLayerFromSelection,
        selectLayerRange,
        clearSelection,
        setActiveLayerId,
        setRenamingLayerId,
        setRenamingInspectorPanelId,
        setActiveFeatureId,
    },
} = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        resetSidePanelState: (state) => {
            state.isSidePanelCollapsed = false
            state.sidePanelWidth = sidePanelMinWidth
            state.sidePanelMode = "Layers"
            state.isSearchAndFilterCollapsed = true
            state.searchLayersValue = ""
            state.sortLayersValue = "None"
            state.filterLayersValue = []
            state.filterLayersCreatorId = undefined
            state.searchAssetsValue = ""
            state.sortAssetsValue = "Name"
            state.filterAssetsValue = []
            state.searchPDFsValue = ""
            state.sortPDFsValue = "None"
            state.filterPDFsValue = []
            state.searchArchivedLayersValue = ""
            state.selectedArchivedLayerIds = []
            state.selectedLayerIds = []
            state.renamingLayerId = undefined
            state.renamingInspectorPanelId = undefined
            state.activeLayerId = undefined
            state.activeFeatureId = undefined
        },
        collapseSidePanel: (state) => {
            state.isSidePanelCollapsed = true
        },
        expandSidePanel: (state) => {
            state.isSidePanelCollapsed = false
        },
        setSidePanelWidth: (state, action: { payload: { width: number } }) => {
            state.sidePanelWidth = action.payload.width
        },
        setSidePanelMode: (state, action: { payload: { mode: SidePanelMode } }) => {
            state.selectedLayerIds = []
            state.sidePanelMode = action.payload.mode
        },
        expandSearchAndFilter: (state) => {
            state.isSearchAndFilterCollapsed = false
        },
        collapseSearchAndFilter: (state) => {
            state.isSearchAndFilterCollapsed = true
        },
        setSearchLayersValue: (state, action: { payload: string }) => {
            state.searchLayersValue = action.payload
        },
        setSortLayersValue: (state, action: { payload: LayersSortOptions }) => {
            state.sortLayersValue = action.payload
        },
        setFilterLayersValue: (state, action: { payload: string[] }) => {
            state.filterLayersValue = action.payload
        },
        setFilterLayersCreatorId: (state, action: { payload: string | undefined }) => {
            state.filterLayersCreatorId = action.payload
        },
        setSearchAssetsValue: (state, action: { payload: string }) => {
            state.searchAssetsValue = action.payload
        },
        setSortAssetsValue: (state, action: { payload: AssetsSortOptions }) => {
            state.sortAssetsValue = action.payload
        },
        setFilterAssetsValue: (state, action: { payload: string[] }) => {
            state.filterAssetsValue = action.payload
        },
        setSearchPDFsValue: (state, action: { payload: string }) => {
            state.searchPDFsValue = action.payload
        },
        setSortPDFsValue: (state, action: { payload: PDFsSortOptions }) => {
            state.sortPDFsValue = action.payload
        },
        setFilterPDFsValue: (state, action: { payload: string[] }) => {
            state.filterPDFsValue = action.payload
        },
        setSearchArchivedLayersValue: (state, action: { payload: string }) => {
            state.searchArchivedLayersValue = action.payload
        },
        addArchivedLayersToSelection: (state, action: { payload: { ids: string[] } }) => {
            action.payload.ids.forEach((id) => {
                if (!state.selectedArchivedLayerIds.includes(id)) {
                    state.selectedArchivedLayerIds.push(id)
                }
            })
        },
        removeArchivedLayersFromSelection: (state, action: { payload: { ids: string[] } }) => {
            action.payload.ids.forEach((id) => {
                if (state.selectedArchivedLayerIds.includes(id)) {
                    state.selectedArchivedLayerIds.splice(
                        state.selectedArchivedLayerIds.findIndex((i) => i === id),
                        1
                    )
                }
            })
        },
        setIsUnarchiving: (state, action: { payload: boolean }) => {
            state.isUnarchiving = action.payload
        },
        selectSingleLayer: (state, action: { payload: { id: string } }) => {
            state.selectedLayerIds = [action.payload.id]
        },
        selectMultipleLayers: (state, action: { payload: { ids: string[] } }) => {
            state.selectedLayerIds = action.payload.ids
        },
        addLayerToSelection: (state, action: { payload: { id: string } }) => {
            state.selectedLayerIds = [action.payload.id, ...state.selectedLayerIds]
        },
        removeLayerFromSelection: (state, action: { payload: { id: string } }) => {
            state.selectedLayerIds = state.selectedLayerIds.filter((id) => id !== action.payload.id)
        },
        selectLayerRange: (
            state,
            action: {
                payload: {
                    id: string
                    dataFlatMap: Record<string, SidePanelLayerOrGroup>
                    sortedEntities: SidePanelLayerOrGroup[]
                }
            }
        ) => {
            const { id, dataFlatMap, sortedEntities } = action.payload
            const siblings = getSiblings(id, dataFlatMap, sortedEntities)
            const startingId = state.selectedLayerIds.at(-1) || id
            const startingIndex = siblings.findIndex((sibling) => sibling.id === startingId)
            const endingIndex = siblings.findIndex((sibling) => sibling.id === id)
            if (startingIndex === -1 || endingIndex === -1) return
            state.selectedLayerIds = []
            if (startingIndex > endingIndex) {
                for (let i = endingIndex; i <= startingIndex; i++) {
                    state.selectedLayerIds = state.selectedLayerIds.concat(siblings[i].id)
                }
            } else {
                for (let i = endingIndex; i >= startingIndex; i--) {
                    state.selectedLayerIds = state.selectedLayerIds.concat(siblings[i].id)
                }
            }
        },
        clearSelection: (state) => {
            state.selectedLayerIds = []
        },
        setActiveLayerId: (state, action: { payload: { id: string | undefined } }) => {
            state.activeLayerId = action.payload.id
        },
        setRenamingLayerId: (state, action: { payload: { id: string | undefined } }) => {
            state.renamingLayerId = action.payload.id
        },
        setRenamingInspectorPanelId: (state, action: { payload: { id: string | undefined } }) => {
            state.renamingInspectorPanelId = action.payload.id
        },
        setActiveFeatureId: (
            state,
            action: { payload: { feature: NovaPluginIds | undefined } }
        ) => {
            state.activeFeatureId = action.payload.feature
        },
    },
})

export const SidePanelReducer = { [sliceName]: reducer }

export {
    resetSidePanelState,
    collapseSidePanel,
    expandSidePanel,
    setSidePanelWidth,
    setSidePanelMode,
    expandSearchAndFilter,
    collapseSearchAndFilter,
    setSearchLayersValue,
    setSortLayersValue,
    setFilterLayersValue,
    setFilterLayersCreatorId,
    setSearchAssetsValue,
    setSortAssetsValue,
    setFilterAssetsValue,
    setSearchPDFsValue,
    setSortPDFsValue,
    setFilterPDFsValue,
    setSearchArchivedLayersValue,
    addArchivedLayersToSelection,
    removeArchivedLayersFromSelection,
    setIsUnarchiving,
    selectSingleLayer,
    selectMultipleLayers,
    addLayerToSelection,
    removeLayerFromSelection,
    selectLayerRange,
    clearSelection,
    setActiveLayerId,
    setRenamingLayerId,
    setRenamingInspectorPanelId,
    setActiveFeatureId,
}

const root = (state: RootState) => state[sliceName]

export const selectIsSidePanelCollapsed = createSelector(
    [root],
    (state) => state.isSidePanelCollapsed
)
export const selectSidePanelWidth = createSelector([root], (state) => state.sidePanelWidth)
export const selectSidePanelMode = createSelector([root], (state) => state.sidePanelMode)
export const selectIsSearchAndFilterCollapsed = createSelector(
    [root],
    (state) => state.isSearchAndFilterCollapsed
)
export const selectSearchLayersValue = createSelector([root], (state) => state.searchLayersValue)
export const selectSortLayersValue = createSelector([root], (state) => state.sortLayersValue)
export const selectFilterLayersValue = createSelector([root], (state) => state.filterLayersValue)
export const selectFilterLayersCreatorId = createSelector(
    [root],
    (state) => state.filterLayersCreatorId
)
export const selectSearchAssetsValue = createSelector([root], (state) => state.searchAssetsValue)
export const selectSortAssetsValue = createSelector([root], (state) => state.sortAssetsValue)
export const selectFilterAssetsValue = createSelector([root], (state) => state.filterAssetsValue)
export const selectSearchPDFsValue = createSelector([root], (state) => state.searchPDFsValue)
export const selectSortPDFsValue = createSelector([root], (state) => state.sortPDFsValue)
export const selectFilterPDFsValue = createSelector([root], (state) => state.filterPDFsValue)
export const selectSearchArchivedLayersValue = createSelector(
    [root],
    (state) => state.searchArchivedLayersValue
)

export const selectSelectedArchivedLayerIds = createSelector(
    [root],
    (state) => state.selectedArchivedLayerIds
)
export const selectIsUnarchiving = createSelector([root], (state) => state.isUnarchiving)

export const selectSelectedLayerIds = createSelector([root], (state) => state.selectedLayerIds)
export const selectSelectedLayers = createSelector(
    [selectAllEntitiesMap, selectPdfEntitiesMap, selectSelectedLayerIds],
    (entityById, pdfsById, selectedIds) =>
        selectedIds.map((id) => entityById[id] || pdfsById[id]).filter((layer) => !!layer)
)
export const selectSelectedPDFs = createSelector(
    [selectPDFJobsMap, selectSelectedLayerIds],
    (pdfs, ids) => ids.map((id) => pdfs[id]).filter(Boolean)
)
export const selectLastSelectedLayerId = createSelector([selectSelectedLayerIds], (layerIds) =>
    layerIds.at(-1)
)
export const selectLastSelectedLayer = createSelector(
    [selectAllEntitiesMap, selectLastSelectedLayerId],
    (entityById, lastSelectedLayerId) =>
        lastSelectedLayerId ? entityById[lastSelectedLayerId] : undefined
)
export const selectActiveLayerId = createSelector([root], (state) => state.activeLayerId)
export const selectActiveFeatureId = createSelector([root], (state) => state.activeFeatureId)
export const selectRenamingLayerId = createSelector([root], (state) => state.renamingLayerId)
export const selectRenamingInspectorPanelId = createSelector(
    [root],
    (state) => state.renamingInspectorPanelId
)
export const selectLockedLayersMap = createSelector([selectAllEntities], (entities) => {
    return entities.reduce(
        (acc, cur) => {
            acc[cur.id] = !!cur.locked
            return acc
        },
        {} as Record<string, boolean>
    )
})
